import { baseApi } from "./baseApi";

export interface ProjectImageRecord {
  id: string;
  title: string;
  category: string;
  imageUrl: string;
  projectId?: number;
  description?: string;
  createdAt?: string;
}

interface ImageApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors: string[];
}

export interface UploadProjectImagePayload {
  file: File;
  title?: string;
  category?: string;
  projectId?: number;
}

const normalizeImage = (raw: Record<string, any>): ProjectImageRecord => ({
  id: String(raw.id ?? raw.imageId ?? `IMG-${Date.now()}`),
  title: String(raw.title ?? raw.imageName ?? raw.name ?? ""),
  category: String(raw.category ?? raw.imageCategory ?? "General"),
  imageUrl: String(raw.imageUrl ?? raw.url ?? raw.filePath ?? raw.imagePath ?? ""),
  projectId: raw.projectId != null ? Number(raw.projectId) : undefined,
  description: raw.description ? String(raw.description) : undefined,
  createdAt: raw.createdAt ? String(raw.createdAt) : undefined,
});

const unwrap = <T>(res: ImageApiResponse<T> | unknown): T => {
  const r = res as ImageApiResponse<T>;
  return (r?.data ?? res) as T;
};

export interface FetchImagesParams {
  category?: string;
  projectId?: number;
}

// ── Page-Images API types ──────────────────────────────────────────────────
export type PageName = "Residential" | "Commercial" | "Industrial" | "General" | "Cement" | "Steel" | "Paint" | "Tiles";

export interface PageSummary {
  page: PageName;
  imageCount: number;
  thumbnailUrl?: string;
}

export interface UpdatePageImagePayload {
  page: string;
  id: number;
  title: string;
  description?: string;
  isFeatured: boolean;
  sortOrder: number;
}

export interface ReorderItem {
  id: number;
  sortOrder: number;
}

export const projectImagesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Public (no-auth) endpoint for website pages ─────────────────────────
    fetchPublicProjectImages: builder.query<ProjectImageRecord[], string | undefined>({
      query: (category) => {
        const qs = category ? `?category=${encodeURIComponent(category)}` : "";
        return { url: `/api/public/project-images${qs}`, method: "GET" };
      },
      transformResponse: (res: unknown) => {
        const r = res as { success?: boolean; data?: unknown };
        const data = r?.data ?? res;
        if (Array.isArray(data)) return data.map((item) => normalizeImage(item as Record<string, any>));
        return [];
      },
      providesTags: [{ type: "ProjectImage" as const, id: "PUBLIC_LIST" }],
    }),

    fetchProjectImages: builder.query<ProjectImageRecord[], FetchImagesParams | void>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.category) qs.set("category", params.category);
        if (params?.projectId !== undefined) qs.set("projectId", String(params.projectId));
        const q = qs.toString();
        return { url: q ? `/ProjectImage?${q}` : "/ProjectImage", method: "GET" };
      },
      transformResponse: (res: ImageApiResponse<unknown> | unknown) => {
        const data = unwrap(res);
        if (Array.isArray(data)) {
          return data.map((item) => normalizeImage(item as Record<string, any>));
        }
        return [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "ProjectImage" as const, id })),
              { type: "ProjectImage" as const, id: "LIST" },
            ]
          : [{ type: "ProjectImage" as const, id: "LIST" }],
    }),

    uploadProjectImage: builder.mutation<ProjectImageRecord, UploadProjectImagePayload>({
      query: ({ file, title, category, projectId }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (title) formData.append("title", title);
        if (category) formData.append("category", category);
        if (projectId !== undefined) formData.append("projectId", String(projectId));
        return { url: "/ProjectImage/upload", method: "POST", body: formData };
      },
      transformResponse: (res: ImageApiResponse<unknown> | unknown) =>
        normalizeImage((unwrap(res) ?? {}) as Record<string, any>),
      invalidatesTags: [{ type: "ProjectImage" as const, id: "LIST" }],
    }),

    updateProjectImage: builder.mutation<ProjectImageRecord, { id: string; title?: string; category?: string; description?: string }>({
      query: ({ id, ...body }) => ({
        url: `/ProjectImage/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: (res: ImageApiResponse<unknown> | unknown) =>
        normalizeImage((unwrap(res) ?? {}) as Record<string, any>),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ProjectImage" as const, id },
        { type: "ProjectImage" as const, id: "LIST" },
      ],
    }),

    deleteProjectImage: builder.mutation<void, string>({
      query: (id) => ({ url: `/ProjectImage/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "ProjectImage" as const, id },
        { type: "ProjectImage" as const, id: "LIST" },
      ],
    }),

    // ── Page-scoped endpoints ────────────────────────────────────────────────
    fetchPageSummaries: builder.query<PageSummary[], void>({
      query: () => ({ url: "/api/page-images", method: "GET" }),
      transformResponse: (res: unknown) => {
        const r = res as { success?: boolean; data?: unknown };
        const data = r?.data ?? res;
        return Array.isArray(data) ? (data as PageSummary[]) : [];
      },
      providesTags: [{ type: "ProjectImage" as const, id: "PAGE_SUMMARIES" }],
    }),

    fetchPageImages: builder.query<ProjectImageRecord[], string>({
      query: (page) => ({ url: `/api/page-images/${encodeURIComponent(page)}`, method: "GET" }),
      transformResponse: (res: unknown) => {
        const r = res as { success?: boolean; data?: unknown };
        const data = r?.data ?? res;
        if (Array.isArray(data)) return data.map((item) => normalizeImage(item as Record<string, any>));
        return [];
      },
      providesTags: (_r, _e, page) => [{ type: "ProjectImage" as const, id: `PAGE_${page.toUpperCase()}` }],
    }),

    uploadPageImage: builder.mutation<ProjectImageRecord, { page: string; file: File; title?: string; isFeatured?: boolean }>({
      query: ({ page, file, title, isFeatured }) => {
        const form = new FormData();
        form.append("file", file);
        if (title) form.append("title", title);
        if (isFeatured) form.append("isFeatured", "true");
        return { url: `/api/page-images/${encodeURIComponent(page)}/upload`, method: "POST", body: form };
      },
      transformResponse: (res: unknown) =>
        normalizeImage(((res as { data?: unknown })?.data ?? res ?? {}) as Record<string, any>),
      invalidatesTags: (_r, _e, { page }) => [
        { type: "ProjectImage" as const, id: `PAGE_${page.toUpperCase()}` },
        { type: "ProjectImage" as const, id: "PAGE_SUMMARIES" },
        { type: "ProjectImage" as const, id: "PUBLIC_LIST" },
      ],
    }),

    updatePageImage: builder.mutation<ProjectImageRecord, UpdatePageImagePayload>({
      query: ({ page, id, ...body }) => ({
        url: `/api/page-images/${encodeURIComponent(page)}/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: (res: unknown) =>
        normalizeImage(((res as { data?: unknown })?.data ?? res ?? {}) as Record<string, any>),
      invalidatesTags: (_r, _e, { page }) => [
        { type: "ProjectImage" as const, id: `PAGE_${page.toUpperCase()}` },
        { type: "ProjectImage" as const, id: "PUBLIC_LIST" },
      ],
    }),

    deletePageImage: builder.mutation<void, { page: string; id: number }>({
      query: ({ page, id }) => ({
        url: `/api/page-images/${encodeURIComponent(page)}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { page }) => [
        { type: "ProjectImage" as const, id: `PAGE_${page.toUpperCase()}` },
        { type: "ProjectImage" as const, id: "PAGE_SUMMARIES" },
        { type: "ProjectImage" as const, id: "PUBLIC_LIST" },
      ],
    }),

    reorderPageImages: builder.mutation<void, { page: string; items: ReorderItem[] }>({
      query: ({ page, items }) => ({
        url: `/api/page-images/${encodeURIComponent(page)}/reorder`,
        method: "PUT",
        body: items,
      }),
      invalidatesTags: (_r, _e, { page }) => [
        { type: "ProjectImage" as const, id: `PAGE_${page.toUpperCase()}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useFetchPublicProjectImagesQuery,
  useFetchProjectImagesQuery,
  useUploadProjectImageMutation,
  useUpdateProjectImageMutation,
  useDeleteProjectImageMutation,
  useFetchPageSummariesQuery,
  useFetchPageImagesQuery,
  useUploadPageImageMutation,
  useUpdatePageImageMutation,
  useDeletePageImageMutation,
  useReorderPageImagesMutation,
} = projectImagesApi;
